import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';
import {
  Account,
  useSideOverContext,
  useDeleteLoan,
  useDeleteBusiness,
  useDeleteInvestment,
  useDeleteCashAccount,
  useDeleteRealEstateProperty,
  useDeletePersonalPropertyItemMutation,
  useGraphqlClient,
  useDeleteNoteReceivableMutation,
  ttDetailsKeys,
  displayAccountType,
  ElementsButton,
} from '../..';

interface DeleteAccountModalProps {
  show: boolean;
  account: Account;
  householdID: string;
  onClose: () => void;
  onAccountDeleted?: (account: Account) => void;
}

export function DeleteAccountModal({ onClose, onAccountDeleted, show, account, householdID }: DeleteAccountModalProps) {
  const queryClient = useQueryClient();
  const { t: tLoan } = useTranslation('loan');
  const { closeSideOver } = useSideOverContext();
  const { t: tCommon } = useTranslation('common');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tRealEstate } = useTranslation('realEstate');
  const { t: tCashAccount } = useTranslation('cashAccount');
  const { t: taccountSideover } = useTranslation('accountSideover');

  const { deleteLoan } = useDeleteLoan();
  const { deleteBusiness } = useDeleteBusiness();
  const { deleteInvestment } = useDeleteInvestment();
  const { deleteCashAccount } = useDeleteCashAccount();
  const { deleteRealEstateProperty } = useDeleteRealEstateProperty();
  const deletePersonalPropertyItem = useDeletePersonalPropertyItemMutation(useGraphqlClient());
  const deleteNoteReceivable = useDeleteNoteReceivableMutation(useGraphqlClient());

  function deleteAccount() {
    const onSuccess = async (invalidateTtDetails = false) => {
      onAccountDeleted?.(account);
      if (invalidateTtDetails) {
        await queryClient.invalidateQueries(ttDetailsKeys.base);
      }
      onClose();
      closeSideOver();
    };
    const completeMutation = (mutation: Promise<unknown>, invalidateTtDetails = false) => {
      void mutation.then(() => onSuccess(invalidateTtDetails)).catch(() => undefined);
    };

    switch (account.__typename) {
      case 'CashAccount':
        return completeMutation(deleteCashAccount.mutateAsync({ householdID, id: account.id }));
      case 'Investment':
        return completeMutation(deleteInvestment.mutateAsync({ householdID, id: account.id }));
      case 'Business':
        return completeMutation(deleteBusiness.mutateAsync({ householdID, id: account.id }));
      case 'RealEstate':
        return completeMutation(deleteRealEstateProperty.mutateAsync({ householdID, id: account.id }));
      case 'Loan':
        return completeMutation(deleteLoan.mutateAsync({ householdID, id: account.id }));
      case 'PersonalPropertyItem':
        return completeMutation(
          deletePersonalPropertyItem.mutateAsync({ input: { householdID, id: account.id } }),
          true
        );
      case 'NoteReceivable':
        return completeMutation(deleteNoteReceivable.mutateAsync({ input: { householdID, id: account.id } }), true);
    }
  }

  const isDeleting =
    deleteCashAccount.isLoading ||
    deleteInvestment.isLoading ||
    deleteBusiness.isLoading ||
    deleteLoan.isLoading ||
    deleteRealEstateProperty.isLoading ||
    deletePersonalPropertyItem.isLoading ||
    deleteNoteReceivable.isLoading;

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" onClose={onClose} className="fixed inset-0 right-0 z-50 overflow-y-auto">
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }} />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {account ? (
              <div
                className="absolute bottom-0 left-1/2 my-8 transform -translate-x-1/2 w-[calc(100%-2rem)] max-w-[616px] h-auto overflow-hidden rounded-2xl bg-darkMode-gray-floatingMenu p-6 text-left align-middle font-bold shadow-xl transition-all 
                       md:translate-x-0 md:left-auto md:right-0 md:w-[616px] md:h-[255px] md:mx-[32px] md:p-8"
              >
                <Dialog.Title as="h3" className="text-xl md:text-2xl text-white">
                  {taccountSideover('delete-account-heading', {
                    name: account.name,
                  })}
                </Dialog.Title>
                <div className="mt-2">
                  <p
                    className="text-md text-darkMode-gray-light"
                    style={{
                      lineHeight: '1.44',
                      fontSize: '18px',
                      marginTop: '12px',
                      fontWeight: '400',
                    }}
                  >
                    {taccountSideover('delete-account-body', {
                      accountType: displayAccountType(
                        account,
                        tLoan,
                        tCashAccount,
                        tCommon,
                        tInvestment,
                        tRealEstate
                      ).toLowerCase(),
                    })}
                  </p>
                </div>
                <div className="mt-6 md:mt-8 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex rounded-lg border border-palette-600 font-bold text-palette-600"
                    style={{
                      padding: '10px 20px 10px 16px',
                      height: '40px',
                      borderRadius: '20px',
                      fontSize: '15px',
                      lineHeight: 'normal',
                    }}
                    onClick={onClose}
                  >
                    {tCommon('cancel')}
                  </button>
                  <ElementsButton
                    theme="danger"
                    className="ml-4"
                    onClick={deleteAccount}
                    isLoading={isDeleting}
                    label={taccountSideover('delete-account-button', {
                      text: displayAccountType(account, tLoan, tCashAccount, tCommon, tInvestment, tRealEstate),
                    })}
                  />
                </div>
              </div>
            ) : null}
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
